import * as React from "react"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Wave 3 — PageHeader
// Standard section heading (title + subtitle + action slot).
// Replaces the 8 heading variants today (text-3xl vs text-2xl, font-display
// used inconsistently, button placement floating, etc — see DESIGN_SYSTEM.md §1.4).
//
// Props:
//   icon     — lucide icon component (optional)
//   title    — headline string (required; renders as text-h1)
//   subtitle — supporting description (optional)
//   actions  — ReactNode slot, aligned right (optional, typically Buttons)
//   tone     — domain accent for the icon circle (default "neutral")

const iconTone = cva("w-10 h-10 rounded-md flex items-center justify-center shrink-0", {
  variants: {
    tone: {
      neutral:  "bg-surface-card-hover text-foreground-muted",
      task:     "bg-domain-task/10    text-domain-task",
      reminder: "bg-domain-reminder/10 text-domain-reminder",
      habit:    "bg-domain-habit/10   text-domain-habit",
      project:  "bg-domain-project/10 text-domain-project",
      finance:  "bg-domain-finance/10 text-domain-finance",
      note:     "bg-domain-note/10    text-domain-note",
      automation: "bg-domain-automation/10 text-domain-automation",
    },
  },
  defaultVariants: { tone: "neutral" },
})

export function PageHeader({ icon: Icon, title, subtitle, actions, tone, className }) {
  return (
    <header className={cn("flex items-start justify-between gap-6 mb-6", className)}>
      <div className="flex items-center gap-4 min-w-0">
        {Icon && (
          <div className={iconTone({ tone })}>
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-h1 text-foreground truncate">{title}</h1>
          {subtitle && (
            <p className="text-body text-foreground-muted mt-1 truncate">{subtitle}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </header>
  )
}
